import { useEffect, useRef, useState } from 'react';
import type { CapturedPhoto, ElevationCode, ElevationCounts, ShotContext } from './types';
import type { Taxonomy } from '../lib/taxonomy';
import { isSpeechSupported, useSpeechRecognition } from '../lib/voice';
import { loadBlob } from '../lib/blobStore';
import { addDescription } from '../lib/taxonomy';
import { TaxonomyDrawer } from './TaxonomyDrawer';

const ELEVATION_LABELS: Record<ElevationCode, string> = {
  front: 'Front Elevation',
  right: 'Right Elevation',
  rear: 'Rear Elevation',
  left: 'Left Elevation',
};

interface Props {
  photo: CapturedPhoto;
  taxonomy: Taxonomy;
  defaults: Taxonomy;
  elevationCounts: ElevationCounts;
  onConfirm: (photo: CapturedPhoto) => void;
  onShootAgain: () => void;
  onTaxonomyMutated: (next: Taxonomy) => void;
}

function splitDescription(s: string): string[] {
  return s.split(', ').map((x) => x.trim()).filter(Boolean);
}

export function PostShotOverlay({
  photo,
  taxonomy,
  defaults,
  elevationCounts,
  onConfirm,
  onShootAgain,
  onTaxonomyMutated,
}: Props) {
  const [context, setContext] = useState<ShotContext>(photo.context);
  const [noteText, setNoteText] = useState(photo.noteText);
  const [flagged, setFlagged] = useState(photo.flagged);
  const [noteOpen, setNoteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const swipeRef = useRef<{ x: number; y: number; triggered: boolean } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState('');
  const [savingAdd, setSavingAdd] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const blob = await loadBlob(photo.blobKey);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch {
        // fall through; overlay will just show a dark background
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.blobKey]);

  const isElevationLabel = Object.values(ELEVATION_LABELS).includes(context.label);
  const showElevations = isElevationLabel && !context.locked;

  const selected = splitDescription(context.description);
  const cat = taxonomy.categories.find((c) => c.name === context.category);
  const lab = cat?.labels.find((l) => l.name === context.label);
  const available = lab?.descriptions ?? [];

  function toggleDescription(d: string) {
    if (context.locked) return;
    const next = selected.includes(d)
      ? selected.filter((x) => x !== d)
      : [...selected, d];
    setContext({ ...context, description: next.join(', ') });
  }

  function changeElevation(elev: ElevationCode) {
    if (context.locked) return;
    setContext({ ...context, elevation: elev, label: ELEVATION_LABELS[elev] });
  }

  function appendVoice(text: string) {
    setNoteText((prev) => (prev ? `${prev} ${text}` : text));
  }

  const speechSupported = isSpeechSupported();
  const speech = useSpeechRecognition(appendVoice);

  function doConfirm() {
    onConfirm({ ...photo, context, noteText, flagged });
  }

  function onChipTouchStart(e: React.TouchEvent) {
    if (e.touches.length !== 1) return;
    swipeRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      triggered: false,
    };
  }
  function onChipTouchMove(e: React.TouchEvent) {
    if (e.touches.length !== 1 || !swipeRef.current || swipeRef.current.triggered) return;
    const dx = e.touches[0].clientX - swipeRef.current.x;
    const dy = e.touches[0].clientY - swipeRef.current.y;
    if (dy < -30 && Math.abs(dy) > Math.abs(dx) * 1.5) {
      swipeRef.current.triggered = true;
      setDrawerOpen(true);
    }
  }
  function onChipTouchEnd() {
    swipeRef.current = null;
  }

  async function submitInlineAdd() {
    const v = addText.trim();
    if (!v || savingAdd || context.locked) return;
    setSavingAdd(true);
    try {
      const next = await addDescription(context.category, context.label, v);
      onTaxonomyMutated(next);
      setContext({
        ...context,
        description: [...selected.filter((x) => x !== v), v].join(', '),
      });
      setAddText('');
      setAdding(false);
    } finally {
      setSavingAdd(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black text-white">
      {previewUrl && (
        <img
          src={previewUrl}
          alt="Captured photo preview"
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
      <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-black/60 via-black/10 to-black/75">
      {/* Top: flag + shoot again */}
      <div className="flex items-center justify-end gap-2 p-4">
        <button
          onClick={() => setFlagged((f) => !f)}
          className={`rounded-full px-3 py-1.5 text-sm ${
            flagged ? 'bg-red-500' : 'bg-white/10'
          }`}
        >
          {flagged ? 'Flagged' : 'Flag'}
        </button>
        <button
          onClick={onShootAgain}
          className="rounded-full bg-white/10 px-3 py-1.5 text-sm"
        >
          Shoot Again
        </button>
      </div>

      {/* Category · Label header */}
      <div className="flex justify-center px-4">
        <div className="rounded-full bg-black/60 px-3 py-1 text-xs uppercase tracking-wide opacity-90">
          {context.category} · {context.label}
        </div>
      </div>

      {/* Selected descriptions — tap the × to remove */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 px-4 py-3">
          {selected.map((d) => (
            <button
              key={d}
              onClick={() => toggleDescription(d)}
              disabled={context.locked}
              className="flex items-center gap-1 rounded-full bg-bracket-blue px-3 py-1 text-sm disabled:opacity-60"
              aria-label={`Remove ${d}`}
            >
              {d}
              {!context.locked && <span className="text-xs opacity-70">×</span>}
            </button>
          ))}
        </div>
      )}

      {/* Swipe-up hint */}
      {!context.locked && (
        <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-white/40" />
      )}

      {/* All descriptions for current label — scrollable, tap to toggle, swipe up for full menu */}
      {!context.locked && (
        <div
          className="-mx-4 overflow-x-auto"
          onTouchStart={onChipTouchStart}
          onTouchMove={onChipTouchMove}
          onTouchEnd={onChipTouchEnd}
        >
          <div className="flex items-center gap-2 px-4 pb-1">
            {available.map((d) => {
              const isSelected = selected.includes(d);
              return (
                <button
                  key={d}
                  onClick={() => toggleDescription(d)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
                    isSelected
                      ? 'border-bracket-blue bg-bracket-blue text-white'
                      : 'border-white/30 bg-black/50 text-white/85'
                  }`}
                >
                  {d}
                </button>
              );
            })}
            {adding ? (
              <div className="flex shrink-0 items-center gap-1">
                <input
                  autoFocus
                  value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submitInlineAdd();
                    if (e.key === 'Escape') {
                      setAdding(false);
                      setAddText('');
                    }
                  }}
                  placeholder="new description"
                  disabled={savingAdd}
                  className="w-40 rounded-full border border-white/50 bg-black/60 px-3 py-1.5 text-sm text-white outline-none"
                />
                <button
                  onClick={() => void submitInlineAdd()}
                  disabled={savingAdd || !addText.trim()}
                  className="rounded-full bg-eva-teal px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                >
                  {savingAdd ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="shrink-0 rounded-full border border-dashed border-white/50 px-3 py-1.5 text-sm text-white/85"
              >
                + new
              </button>
            )}
          </div>
        </div>
      )}

      {/* Secondary: open full taxonomy drawer */}
      {!context.locked && (
        <div className="mt-2 text-center">
          <button
            onClick={() => setDrawerOpen(true)}
            className="text-xs underline opacity-60"
          >
            Full menu (swipe up from chips)
          </button>
        </div>
      )}

      {/* Elevation switcher */}
      {showElevations && (
        <div className="mt-3 flex justify-center gap-2 px-4">
          {(Object.keys(ELEVATION_LABELS) as ElevationCode[]).map((e) => (
            <button
              key={e}
              onClick={() => changeElevation(e)}
              className={`rounded-lg px-3 py-2 text-sm ${
                context.elevation === e ? 'bg-bracket-blue text-white' : 'bg-white/10'
              }`}
            >
              <div className="capitalize">{e}</div>
              <div className="text-[11px] opacity-70">{elevationCounts[e]}</div>
            </button>
          ))}
        </div>
      )}

      {/* Note field */}
      <div className="mt-auto px-4 pb-4">
        {!noteOpen ? (
          <button
            onClick={() => setNoteOpen(true)}
            className="w-full rounded-lg bg-white/10 px-4 py-3 text-left text-sm"
          >
            {noteText || 'Add note...'}
          </button>
        ) : (
          <div className="space-y-2 rounded-lg bg-white/10 p-3">
            <textarea
              autoFocus
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
              className="w-full resize-none rounded bg-black/40 p-2 text-sm outline-none"
              placeholder="Note for this photo"
            />
            {speech.interim && (
              <div className="text-xs italic opacity-70">{speech.interim}</div>
            )}
            <div className="flex items-center gap-2">
              {speechSupported && (
                <button
                  onPointerDown={speech.start}
                  onPointerUp={speech.stop}
                  onPointerLeave={speech.stop}
                  className={`rounded-full px-4 py-2 text-sm ${
                    speech.isRecording ? 'bg-red-500' : 'bg-white/20'
                  }`}
                >
                  {speech.isRecording ? 'Listening…' : 'Hold to talk'}
                </button>
              )}
              <button
                onClick={() => setNoteOpen(false)}
                className="ml-auto rounded-full bg-white/20 px-4 py-2 text-sm"
              >
                Done
              </button>
            </div>
          </div>
        )}

        <button
          onClick={doConfirm}
          className="mt-3 w-full rounded-lg bg-eva-teal px-4 py-3 text-base font-semibold"
        >
          Confirm
        </button>
      </div>

      </div>
      {drawerOpen && (
        <TaxonomyDrawer
          taxonomy={taxonomy}
          defaults={defaults}
          current={context}
          onSelect={(next) => setContext(next)}
          onClose={() => setDrawerOpen(false)}
          onTaxonomyMutated={onTaxonomyMutated}
        />
      )}
    </div>
  );
}
